import { useEffect, useRef, useState } from 'react';
import registryService from '../services/registryService';

const LOAD_ERROR = 'Errore nel caricamento dello storico';

/**
 * Storico delle modifiche delle voci di un'anagrafica (paginato lato server): di tutta
 * l'anagrafica o, con `entryId`, di una sola voce. Una risposta fuori ordine viene scartata.
 */
const useRegistryEntryHistory = (registry, entryId, { size = 20 } = {}) => {
  const registryId = registry?.id ?? null;
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  // con un'anagrafica la richiesta parte subito dopo il primo render: niente «nessuna modifica» nel frattempo
  const [loading, setLoading] = useState(!!registry?.id);
  const [error, setError] = useState(null);
  const requestId = useRef(0);

  // Cambio anagrafica o voce: reset durante il render (non in un effect), cosi' la richiesta
  // per il nuovo ambito non parte con la pagina del precedente.
  const scopeKey = `${registryId ?? ''}|${entryId ?? ''}`;
  const [scope, setScope] = useState(scopeKey);
  if (scope !== scopeKey) {
    setScope(scopeKey);
    setPage(0);
    setRows([]);
    setTotalElements(0);
    setError(null);
    setLoading(!!registryId);
  }

  useEffect(() => {
    if (!registryId) {
      // invalida l'eventuale richiesta in volo
      requestId.current += 1;
      setLoading(false);
      return;
    }
    const id = ++requestId.current;
    setLoading(true);
    // Anche un'eccezione sincrona del servizio passa dal catch.
    new Promise((resolve) => resolve(registryService.getEntryHistory(registryId, { page, size, entryId })))
      .then((data) => {
        if (id !== requestId.current) return;
        setRows(Array.isArray(data?.content) ? data.content : []);
        setTotalElements(Number(data?.totalElements) || 0);
        setError(null);
      })
      .catch(() => {
        if (id !== requestId.current) return;
        setRows([]);
        setTotalElements(0);
        setError(LOAD_ERROR);
      })
      .finally(() => {
        if (id === requestId.current) setLoading(false);
      });
  }, [registryId, entryId, page, size]);

  return { rows, totalElements, page, setPage, loading, error };
};

export default useRegistryEntryHistory;
